import { useEffect, useRef } from "react";

interface DragonAtmosphereProps {
  accent: string;
  accentSoft: string;
  lineage: string;
}

interface Rgb {
  r: number;
  g: number;
  b: number;
}

interface Mote {
  x: number;
  y: number;
  size: number;
  speed: number;
  drift: number;
  phase: number;
  alpha: number;
}

const LINEAGE_SEEDS: Record<string, number> = {
  red: 0.12,
  blue: 0.23,
  green: 0.34,
  black: 0.45,
  white: 0.56,
  gold: 0.67,
  silver: 0.78,
  bronze: 0.82,
  brass: 0.91,
  copper: 0.97
};

const LINE_BREATH = new Set(["blue", "black", "bronze", "brass", "copper"]);
const COLD_LINEAGES = new Set(["white", "silver"]);
const VENOM_LINEAGES = new Set(["green", "black"]);
const STORM_LINEAGES = new Set(["blue", "bronze"]);

function hexToRgb(value: string): Rgb {
  const normalized = value.replace("#", "").trim();
  const expanded =
    normalized.length === 3
      ? normalized
          .split("")
          .map((char) => `${char}${char}`)
          .join("")
      : normalized;
  const parsed = Number.parseInt(expanded.slice(0, 6), 16);
  if (Number.isNaN(parsed)) return { r: 201, g: 164, b: 85 };
  return {
    r: (parsed >> 16) & 255,
    g: (parsed >> 8) & 255,
    b: parsed & 255
  };
}

function rgba(color: Rgb, alpha: number): string {
  return `rgba(${color.r}, ${color.g}, ${color.b}, ${alpha})`;
}

function fract(value: number): number {
  return value - Math.floor(value);
}

function seeded(seed: number, index: number): number {
  return fract(Math.sin((index + 1) * 139.71 + seed * 997.13) * 32768.371);
}

function wrap(value: number, max: number): number {
  return ((value % max) + max) % max;
}

function buildMotes(width: number, height: number, seed: number): Mote[] {
  // Dichtheid schaalt met oppervlak: meer drama op desktop, zuinig op mobiel.
  const count = Math.round(Math.min(112, Math.max(30, width / 11)));
  return Array.from({ length: count }, (_, index) => ({
    x: seeded(seed, index) * width,
    y: seeded(seed, index + 51) * height,
    size: 0.8 + seeded(seed, index + 103) * 3.8,
    speed: 8 + seeded(seed, index + 151) * 24,
    drift: 0.35 + seeded(seed, index + 211) * 1.2,
    phase: seeded(seed, index + 307) * Math.PI * 2,
    alpha: 0.2 + seeded(seed, index + 401) * 0.38
  }));
}

function drawBreath(
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
  time: number,
  accent: Rgb,
  soft: Rgb,
  lineage: string
) {
  const sourceX = width * 0.74;
  const sourceY = height * 0.44 + Math.sin(time * 0.7) * 8;
  const gradient = context.createLinearGradient(width * 0.18, height * 0.22, width * 0.92, height * 0.66);
  gradient.addColorStop(0, rgba(soft, 0.01));
  gradient.addColorStop(0.48, rgba(accent, 0.12));
  gradient.addColorStop(1, rgba(soft, 0.28));

  context.save();
  context.globalCompositeOperation = "screen";
  context.filter = "blur(18px)";
  context.fillStyle = gradient;
  context.strokeStyle = gradient;

  if (LINE_BREATH.has(lineage)) {
    context.lineWidth = Math.max(46, height * 0.14);
    context.lineCap = "round";
    context.beginPath();
    context.moveTo(width * 0.18, height * 0.76);
    context.bezierCurveTo(width * 0.38, height * 0.55, width * 0.58, height * 0.36, sourceX, sourceY);
    context.stroke();
  } else {
    context.beginPath();
    context.moveTo(width * 0.18, height * 0.14);
    context.bezierCurveTo(width * 0.42, height * 0.24, width * 0.58, height * 0.3, sourceX, sourceY);
    context.bezierCurveTo(width * 0.58, height * 0.62, width * 0.38, height * 0.82, width * 0.12, height * 0.94);
    context.closePath();
    context.fill();
  }
  context.restore();
}

function drawRibbons(
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
  time: number,
  accent: Rgb,
  soft: Rgb,
  seed: number
) {
  context.save();
  context.globalCompositeOperation = "screen";
  context.lineCap = "round";

  for (let lane = 0; lane < 6; lane += 1) {
    const yBase = height * (0.18 + lane * 0.12) + seeded(seed, lane + 17) * 18;
    context.beginPath();
    for (let step = 0; step <= 54; step += 1) {
      const progress = step / 54;
      const x = width * (-0.05 + progress * 1.12);
      const wave =
        Math.sin(progress * 7.2 + time * (0.52 + lane * 0.04) + lane) * (12 + lane * 1.8) +
        Math.sin(progress * 15 + time * 0.27 + seed * 4) * 5;
      const y = yBase + wave;
      if (step === 0) context.moveTo(x, y);
      else context.lineTo(x, y);
    }
    context.strokeStyle = lane % 2 ? rgba(soft, 0.075) : rgba(accent, 0.09);
    context.lineWidth = 0.8 + lane * 0.18;
    context.shadowColor = rgba(lane % 2 ? soft : accent, 0.28);
    context.shadowBlur = 12;
    context.stroke();
  }
  context.restore();
}

function drawMotes(
  context: CanvasRenderingContext2D,
  motes: Mote[],
  width: number,
  height: number,
  time: number,
  accent: Rgb,
  soft: Rgb,
  lineage: string
) {
  const cold = COLD_LINEAGES.has(lineage);
  context.save();
  context.globalCompositeOperation = cold ? "lighter" : "screen";

  for (const mote of motes) {
    const x = wrap(mote.x + Math.sin(time * mote.drift + mote.phase) * 34 + time * mote.speed * 0.32, width + 90) - 45;
    const y = wrap(mote.y - time * mote.speed * 0.46, height + 90) - 45;
    const pulse = 0.72 + Math.sin(time * 1.8 + mote.phase) * 0.28;
    const radius = mote.size * pulse;
    const color = mote.size > 2.8 ? soft : accent;
    const alpha = mote.alpha * pulse;

    const glow = context.createRadialGradient(x, y, 0, x, y, radius * 6);
    glow.addColorStop(0, rgba(color, alpha));
    glow.addColorStop(0.35, rgba(color, alpha * 0.34));
    glow.addColorStop(1, rgba(color, 0));
    context.fillStyle = glow;
    context.beginPath();
    context.arc(x, y, radius * 6, 0, Math.PI * 2);
    context.fill();

    context.strokeStyle = rgba(color, alpha * 0.7);
    context.lineWidth = Math.max(0.6, radius * 0.34);
    context.beginPath();
    context.moveTo(x - radius * 2.2, y + radius * 0.3);
    context.lineTo(x + radius * 2.8, y - radius * 0.7);
    context.stroke();
  }
  context.restore();
}

function drawElementalAccents(
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
  time: number,
  accent: Rgb,
  soft: Rgb,
  lineage: string,
  seed: number
) {
  context.save();
  context.globalCompositeOperation = "screen";

  if (STORM_LINEAGES.has(lineage)) {
    context.lineCap = "round";
    context.lineJoin = "round";
    for (let bolt = 0; bolt < 3; bolt += 1) {
      const active = fract(time * 0.19 + seeded(seed, bolt) * 2) > 0.58;
      context.globalAlpha = active ? 0.4 : 0.11;
      context.strokeStyle = rgba(soft, active ? 0.55 : 0.18);
      context.lineWidth = active ? 1.2 : 0.55;
      context.shadowBlur = active ? 18 : 8;
      context.shadowColor = rgba(soft, 0.42);
      context.beginPath();
      const startX = width * (0.52 + seeded(seed, bolt + 21) * 0.28);
      const startY = height * (0.1 + seeded(seed, bolt + 41) * 0.24);
      context.moveTo(startX, startY);
      for (let segment = 1; segment <= 5; segment += 1) {
        context.lineTo(
          startX + seeded(seed, bolt * 11 + segment) * 84 - 42,
          startY + segment * 24 + seeded(seed, bolt * 17 + segment) * 18
        );
      }
      context.stroke();
    }
  } else if (COLD_LINEAGES.has(lineage)) {
    context.strokeStyle = rgba(soft, 0.18);
    context.lineWidth = 0.8;
    context.shadowBlur = 12;
    context.shadowColor = rgba(soft, 0.28);
    for (let shard = 0; shard < 14; shard += 1) {
      const x = width * (0.42 + seeded(seed, shard) * 0.5);
      const y = height * (0.16 + seeded(seed, shard + 34) * 0.66);
      const size = 7 + seeded(seed, shard + 82) * 15;
      const rotate = time * 0.08 + seeded(seed, shard + 12) * Math.PI;
      context.save();
      context.translate(x, y);
      context.rotate(rotate);
      context.beginPath();
      context.moveTo(-size, 0);
      context.lineTo(size, 0);
      context.moveTo(0, -size * 0.42);
      context.lineTo(0, size * 0.42);
      context.stroke();
      context.restore();
    }
  } else if (VENOM_LINEAGES.has(lineage)) {
    context.filter = "blur(10px)";
    for (let pool = 0; pool < 5; pool += 1) {
      const x = width * (0.46 + seeded(seed, pool + 4) * 0.46);
      const y = height * (0.28 + seeded(seed, pool + 31) * 0.48);
      const radius = 32 + seeded(seed, pool + 53) * 58;
      const haze = context.createRadialGradient(x, y, 0, x, y, radius);
      haze.addColorStop(0, rgba(soft, 0.13));
      haze.addColorStop(0.55, rgba(accent, 0.07));
      haze.addColorStop(1, rgba(accent, 0));
      context.fillStyle = haze;
      context.beginPath();
      context.ellipse(x, y + Math.sin(time + pool) * 5, radius, radius * 0.36, seeded(seed, pool) * Math.PI, 0, Math.PI * 2);
      context.fill();
    }
  } else {
    context.lineCap = "round";
    context.shadowColor = rgba(soft, 0.36);
    context.shadowBlur = 16;
    for (let tongue = 0; tongue < 10; tongue += 1) {
      const x = width * (0.36 + seeded(seed, tongue + 5) * 0.56);
      const y = height * (0.84 + seeded(seed, tongue + 27) * 0.12);
      const heightScale = 36 + seeded(seed, tongue + 72) * 62;
      const sway = Math.sin(time * 1.2 + tongue) * 10;
      const gradient = context.createLinearGradient(x, y, x + sway, y - heightScale);
      gradient.addColorStop(0, rgba(accent, 0));
      gradient.addColorStop(0.45, rgba(accent, 0.1));
      gradient.addColorStop(1, rgba(soft, 0.32));
      context.strokeStyle = gradient;
      context.lineWidth = 3 + seeded(seed, tongue + 94) * 4;
      context.beginPath();
      context.moveTo(x, y);
      context.bezierCurveTo(x - 18, y - heightScale * 0.3, x + 22 + sway, y - heightScale * 0.65, x + sway, y - heightScale);
      context.stroke();
    }
  }

  context.restore();
}

export function DragonAtmosphere({ accent, accentSoft, lineage }: DragonAtmosphereProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const parent = canvas?.parentElement;
    if (!canvas || !parent) return;

    const context = canvas.getContext("2d", { alpha: true });
    if (!context) return;

    const motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const accentRgb = hexToRgb(accent);
    const softRgb = hexToRgb(accentSoft);
    const seed = LINEAGE_SEEDS[lineage] ?? 0.5;
    let animationFrame = 0;
    let width = 1;
    let height = 1;
    let motes = buildMotes(width, height, seed);

    const resize = () => {
      const bounds = parent.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 1.65);
      width = Math.max(1, bounds.width);
      height = Math.max(1, bounds.height);
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      context.setTransform(dpr, 0, 0, dpr, 0, 0);
      motes = buildMotes(width, height, seed);
      paint(0);
    };

    const paint = (timeMs: number) => {
      const time = motionQuery.matches ? seed * 10 : timeMs * 0.001;
      context.clearRect(0, 0, width, height);
      drawBreath(context, width, height, time, accentRgb, softRgb, lineage);
      drawRibbons(context, width, height, time, accentRgb, softRgb, seed);
      drawElementalAccents(context, width, height, time, accentRgb, softRgb, lineage, seed);
      drawMotes(context, motes, width, height, time, accentRgb, softRgb, lineage);
    };

    const loop = (time: number) => {
      paint(time);
      animationFrame = window.requestAnimationFrame(loop);
    };

    const start = () => {
      window.cancelAnimationFrame(animationFrame);
      if (motionQuery.matches) paint(0);
      else animationFrame = window.requestAnimationFrame(loop);
    };

    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(parent);
    resize();
    start();
    motionQuery.addEventListener("change", start);

    return () => {
      window.cancelAnimationFrame(animationFrame);
      resizeObserver.disconnect();
      motionQuery.removeEventListener("change", start);
    };
  }, [accent, accentSoft, lineage]);

  return <canvas ref={canvasRef} className="dragon-atmosphere" aria-hidden="true" />;
}
